import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { MultiLevelSelect } from 'modules/menu/components/MultiLevelSelect';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { useSelectionData } from '../../../../../hooks/useSelectionData';
import { useWoltSpecials } from '../../../context/WoltSpecialsContext';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { BasicDetails, FormValues } from '../types';
import { getCategoryOptions } from '../utils';

export const Category = ({ sidePanelMode, categoryId, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { values } = useFormState<FormValues>();
  const { categories, selection } = useSelectionData();
  const intl = useIntl();
  const { woltSpecialsCategory, categoriesWithoutWoltSpecials } = useWoltSpecials();

  const isWoltSpecialsCategory =
    typeof woltSpecialsCategory?.id === 'string' && values.categoryId === woltSpecialsCategory?.id;

  const categoryOptions = useMemo(
    () =>
      getCategoryOptions({
        categories: isWoltSpecialsCategory ? categories : categoriesWithoutWoltSpecials,
        primaryLanguage: selection.primary_language,
      }),
    [categories, categoriesWithoutWoltSpecials, isWoltSpecialsCategory, selection.primary_language],
  );

  const categoryDisplayName = useMemo(
    () => categoryOptions.find(co => co.value === categoryId)?.label ?? '-',
    [categoryOptions, categoryId],
  );

  const previewComponent = <DisplayText dir="auto">{categoryDisplayName}</DisplayText>;

  const editComponent = (
    <Field<BasicDetails['categoryId']> name="categoryId">
      {field => (
        <StyledInputContainer>
          <MultiLevelSelect
            {...field.input}
            options={categoryOptions}
            onClearClick={() => field.input.onChange(null)}
            placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
            disabled={!menuPermissions.contentEdit || isWoltSpecialsCategory}
          />
        </StyledInputContainer>
      )}
    </Field>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.category' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      shouldDisplayCreateTag={!values.categoryId}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
